import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Send } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { EmailRecipient } from '../../utils/practiceEmail';
export interface EmailRecipientGroup {
  id: string;
  name: string;
  recipients: EmailRecipient[];
}
interface TrainingEmailModalProps {
  isOpen: boolean;
  title: string;
  // Optional line under the title, e.g. the course
  context?: string;
  groups: EmailRecipientGroup[];
  onClose: () => void;
  onSend: (body: string) => void;
}
export function TrainingEmailModal({
  isOpen,
  title,
  context,
  groups,
  onClose,
  onSend
}: TrainingEmailModalProps) {
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setBody('');
    setError('');
    setIsSending(false);
    setSentCount(null);
  }, [isOpen]);
  const addressCount = groups.reduce((n, g) => n + g.recipients.length, 0);
  const send = () => {
    if (!body.trim()) {
      setError("Scrivi il testo dell'email");
      return;
    }
    setIsSending(true);
    // Simulated sending
    setTimeout(() => {
      onSend(body.trim());
      setIsSending(false);
      setSentCount(addressCount);
    }, 700);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl" data-id="element-1640" data-mp="1s8ojjibop25i">
      {sentCount !== null ? <div className="text-center py-6" data-id="element-1641" data-mp="1w8z1ltcr3j0q">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" data-id="element-1642" data-mp="1ia92k86df99q" />
          <p className="text-lg font-semibold text-seform-navy mt-3" data-id="element-1643" data-mp="1ic3e4vvr5p15">
            Email inviata
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1644" data-mp="1nawqgkzaz9zq">
            Inviata a {sentCount} {sentCount === 1 ? 'indirizzo' : 'indirizzi'}
            {groups.length > 1 && ` per ${groups.length} clienti`}. L'invio è
            registrato nello Storico e azioni di ogni pratica.
          </p>
          <Button className="mt-6" onClick={onClose} data-id="element-1645" data-mp="3nzayc6sl2gkd">
            Chiudi
          </Button>
        </div> : <div className="space-y-5" data-id="element-1646" data-mp="3f4whhzsemxz5">
          {context && <p className="text-sm text-slate-500 -mt-1" data-id="element-1647" data-mp="3f3lcjwn83k6g">{context}</p>}
          <div data-id="element-1648" data-mp="3a6biu4sswmct">
            <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-1649" data-mp="3a4qn4bur2xdd">
              Destinatari
              <span className="font-normal text-slate-500" data-id="element-1650" data-mp="356ysha2q0x5h">
                {' '}
                · {addressCount} {addressCount === 1 ? 'indirizzo' : 'indirizzi'}
              </span>
            </p>
            {groups.length === 0 ? <p className="text-sm text-slate-500" data-id="element-1651" data-mp="2w6adod5i6srj">Nessun cliente iscritto.</p> : <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto" data-id="element-1652" data-mp="2r8s7uvms8tnr">
                {groups.map(g => <li key={g.id} className="px-4 py-3" data-id="element-1653" data-mp="pfb0lt2kdutj">
                    <p className="text-sm font-medium text-seform-navy" data-id="element-1654" data-mp="pdq6g4k5wdw7">{g.name}</p>
                    {g.recipients.length === 0 ? <p className="text-xs text-amber-700 flex items-center gap-1 mt-1" data-id="element-1655" data-mp="11xequiph9802">
                        <AlertTriangle className="h-3.5 w-3.5" data-id="element-1656" data-mp="16od9vq1uok70" /> Nessun indirizzo
                        email disponibile
                      </p> : <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1657" data-mp="e07mswnbqj40">
                        {g.recipients.map(r => <li key={r.email} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-xs" data-id="element-1658" data-mp="iyspr3tyngw5">
                            <span className="text-slate-500" data-id="element-1659" data-mp="s11xjlxugw5u">{r.role}</span>
                            <span className="text-slate-800" data-id="element-1660" data-mp="1zdu6kzwi6n91">{r.email}</span>
                          </li>)}
                      </ul>}
                  </li>)}
              </ul>}
          </div>
          <Textarea label="Testo dell'email" rows={7} value={body} onChange={e => {
        setBody(e.target.value);
        setError('');
      }} description={groups.length > 1 ? 'Lo stesso testo viene inviato a tutti i destinatari' : undefined} error={error} required data-id="element-1661" data-mp="22031puy0acvw" />
          <div className="flex justify-end gap-3 pt-2" data-id="element-1662" data-mp="2fyji6dlo7hr8">
            <Button variant="flat" color="default" onClick={onClose} data-id="element-1663" data-mp="2fvoveff6pkun">
              Annulla
            </Button>
            <Button onClick={send} disabled={addressCount === 0 || isSending} startContent={<Send className="h-4 w-4" data-id="element-1665" data-mp="33mued3gf8t96" />} data-id="element-1664" data-mp="2pnkwlzckhr33">
              {isSending ? 'Invio in corso...' : 'Invia email'}
            </Button>
          </div>
        </div>}
    </Modal>;
}